import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ParticipantesPicker from '../components/ParticipantesPicker';
import { useAuth } from '../contexts/AuthContext';
import { useNotificationContext } from '../contexts/NotificationContext';
import api from '../services/api';
import TicketTimeline from '../components/TicketTimeline';
import ChatInternoModal from '../components/ChatInternoModal';
import EvidenceItem from '../components/EvidenceItem';
import EvidenceModal from '../components/EvidenceModal';

import ActivosAsignadosModal from '../components/ActivosAsignadosModal';
import TicketRecordatorio from '../components/TicketRecordatorio';
import TicketRecordarUsuario from '../components/TicketRecordarUsuario';
import TicketEditarDetalles from '../components/TicketEditarDetalles';
import FeedbackModal from '../components/FeedbackModal';
import { Trash2, AlertCircle, CheckCircle, XCircle, ArrowRightLeft, Mail, Building2, Star } from 'lucide-react';
import { UserCheck, User, Phone, MapPin } from '../components/icons';

// T-5: module-level constant — never recreated on re-render
const TICKET_ESTADOS = ['Pendiente', 'Asignado', 'En Proceso', 'Resuelto', 'Esperando Usuario', 'Cerrado'];

// T-3: pure helper functions at module level — no closure over component state
const getEstadoColor = (ticket) => {
  if (!ticket || !ticket.estado) return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  if (ticket.estado === 'Resuelto' && !ticket.feedback) {
    return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300 border border-purple-200 dark:border-purple-800';
  }
  const colores = {
    'Pendiente': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300',
    'Asignado': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
    'En Proceso': 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
    'Esperando Usuario': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300',
    'Resuelto': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
    'Cerrado': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
  };
  return colores[ticket.estado] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
};

const getDisplayEstado = (ticket) => {
  if (!ticket) return '';
  return ticket.estado;
};

const getPrioridadColor = (prioridad) => {
  const colores = {
    'Baja': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
    'Media': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300',
    'Alta': 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
    'Crítica': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
  };
  return colores[prioridad] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
};

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifyTicketUpdated, notifyTicketAssigned, alertSuccess, alertError } = useNotificationContext();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usuariosSoporte, setUsuariosSoporte] = useState([]);
  const [participantes, setParticipantes] = useState([]);
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [esInterno, setEsInterno] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [nuevaPrioridad, setNuevaPrioridad] = useState('');
  const [alerta, setAlerta] = useState(null); // { tipo: 'success'|'error', mensaje: string }
  const [showChatInterno, setShowChatInterno] = useState(false);
  const [showActualizacion, setShowActualizacion] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isResolvingFeedback, setIsResolvingFeedback] = useState(false);
  const [selectedAssetForModal, setSelectedAssetForModal] = useState(null);

  // Estados de Derivación
  const [showDerivarModal, setShowDerivarModal] = useState(false);
  const [soporteDestinoId, setSoporteDestinoId] = useState('');
  const [motivoDerivacion, setMotivoDerivacion] = useState('');
  const [showRechazarModal, setShowRechazarModal] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [submittingDerivacion, setSubmittingDerivacion] = useState(false);

  const handleDerivarTicket = async (e) => {
    e.preventDefault();
    if (!soporteDestinoId) {
      alertError('Selecciona un técnico de soporte destino.');
      return;
    }
    setSubmittingDerivacion(true);
    try {
      await api.post(`/tickets/${id}/derivar`, {
        soporteDestinoId: parseInt(soporteDestinoId, 10),
        motivo: motivoDerivacion,
      });
      alertSuccess('Solicitud de derivación enviada. El ticket permanecerá asignado hasta que el técnico la acepte.');
      setShowDerivarModal(false);
      setMotivoDerivacion('');
      setSoporteDestinoId('');
      cargarTicket();
    } catch (error) {
      alertError(error.response?.data?.message || 'Error al solicitar la derivación.');
    } finally {
      setSubmittingDerivacion(false);
    }
  };

  const handleResponderDerivacion = async (aceptar, motivoRechazoTexto = '') => {
    try {
      await api.post(`/tickets/${id}/responder-derivacion`, {
        aceptar,
        motivoRechazo: motivoRechazoTexto,
      });
      alertSuccess(aceptar ? 'Derivación aceptada. El ticket fue reasignado.' : 'Derivación rechazada. El ticket se mantiene asignado al técnico actual.');
      setShowRechazarModal(false);
      setMotivoRechazo('');
      cargarTicket();
    } catch (error) {
      alertError(error.response?.data?.message || 'Error al responder a la derivación.');
    }
  };

  const handleCancelarDerivacion = async () => {
    if (!window.confirm('¿Deseas cancelar esta propuesta de derivación?')) return;
    try {
      await api.delete(`/tickets/${id}/cancelar-derivacion`);
      alertSuccess('Propuesta de derivación cancelada.');
      cargarTicket();
    } catch (error) {
      alertError(error.response?.data?.message || 'Error al cancelar la derivación.');
    }
  };
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [activosUsuario, setActivosUsuario] = useState([]);
  const [activoSeleccionado, setActivoSeleccionado] = useState('');
  const [activoRelacionado, setActivoRelacionado] = useState(null);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);

  const handleGoBack = () => {
    const role = user?.role?.toLowerCase();
    if (['admin', 'soporte'].includes(role)) {
      navigate('/tickets');
    } else {
      navigate('/mis-tickets');
    }
  };

  const cargarTicket = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/tickets/${id}`);
      setTicket(response.data);
      setNuevoEstado(response.data.estado);
      setNuevaPrioridad(response.data.prioridad || '');
      api.get(`/tickets/${id}/participantes`).then((r) => setParticipantes(Array.isArray(r.data) ? r.data : [])).catch(() => setParticipantes([]));

      if (response.data.activo) {
        setActivoRelacionado(response.data.activo);
        setActivoSeleccionado(response.data.activo.id.toString());
      } else {
        setActivoRelacionado(null);
        setActivoSeleccionado('');
      }
    } catch (error) {
      console.error('Error cargarTicket:', error);
      if (error.response?.status === 403) {
        alertError('No tienes permiso para ver este ticket.');
        navigate('/mis-tickets');
        return;
      }
      const errorMsg = 'No se pudo cargar el ticket.';
      setAlerta({ tipo: 'error', mensaje: errorMsg });
      alertError(errorMsg);
      setTicket(null);
    } finally {
      setLoading(false);
    }
  }, [id, alertError, navigate]);

  const cargarUsuariosSoporte = useCallback(async () => {
    try {
      const response = await api.get('/tickets/soporte/usuarios');
      setUsuariosSoporte(response.data);
    } catch (error) {
      const errorMsg = 'No se pudo cargar la lista de soporte.';
      setAlerta({ tipo: 'error', mensaje: errorMsg });
      alertError(errorMsg);
    }
  }, [alertError]);

  const cargarActivosUsuario = useCallback(async () => {
    try {
      if (!ticket?.emailSolicitante) return;
      const emailEncoded = encodeURIComponent(ticket.emailSolicitante);
      const response = await api.get(`/activos/usuarios/${emailEncoded}/activos`);
      setActivosUsuario(response.data);
    } catch (error) {
      console.error('Error al cargar los activos del usuario:', error);
    }
  }, [ticket?.emailSolicitante]);

  // T-1: include stable callbacks in deps
  useEffect(() => {
    cargarTicket();
    if (user?.role?.toLowerCase() === 'admin') {
      cargarUsuariosSoporte();
    }
  }, [id, user?.role, cargarTicket, cargarUsuariosSoporte]);

  // T-2: include stable callback in deps
  useEffect(() => {
    if (ticket?.emailSolicitante) {
      cargarActivosUsuario();
    }
  }, [ticket?.emailSolicitante, cargarActivosUsuario]);

  const asignarTicket = async (soporteId) => {
    try {
      await api.put(`/tickets/${id}/asignar`, { soporteId });
      alertSuccess('Ticket asignado correctamente.');
      if (soporteId && soporteId !== 0) {
        const soporteAsignado = usuariosSoporte.find(s => s.id === soporteId);
        if (soporteAsignado) notifyTicketAssigned(ticket, soporteAsignado);
      }
      cargarTicket();
    } catch (error) {
      alertError('Error al asignar ticket.');
    }
  };

  const [comentarioEstado, setComentarioEstado] = useState('');
  const requiereComentario = ['Resuelto', 'Cerrado'].includes(nuevoEstado) && nuevoEstado !== ticket?.estado;

  const cambiarEstado = async () => {
    if (requiereComentario && comentarioEstado.trim().length < 3) {
      alertError(`Escribe un comentario para marcar el ticket como ${nuevoEstado}.`);
      return;
    }
    try {
      if (nuevoEstado === 'Asignado') {
        if (user?.role?.toLowerCase() === 'admin' && !ticket?.asignadoA?.id) {
          alertError('Selecciona un técnico antes de asignar.');
          return;
        }
        if (user?.role?.toLowerCase() === 'soporte' && (!ticket?.asignadoA?.id || ticket.asignadoA.id !== user.id)) {
          await asignarTicket(user.id);
        }
      }
      await api.put(`/tickets/${id}/estado`, { estado: nuevoEstado, ...(requiereComentario ? { comentario: comentarioEstado.trim() } : {}) });
      setComentarioEstado('');
      alertSuccess('Estado actualizado.');
      notifyTicketUpdated(ticket);
      cargarTicket();
    } catch (error) {
      alertError('Error al cambiar estado.');
    }
  };

  const cambiarPrioridad = async () => {
    try {
      if (!nuevaPrioridad || nuevaPrioridad === ticket?.prioridad) return;
      await api.put(`/tickets/${id}/prioridad`, { prioridad: nuevaPrioridad });
      alertSuccess('Prioridad actualizada.');
      notifyTicketUpdated(ticket);
      cargarTicket();
    } catch (error) {
      alertError('Error al cambiar prioridad.');
    }
  };

  const toggleAutoClose = async (activar) => {
    try {
      await api.put(`/tickets/${id}/programar-cierre`, { programar: activar });
      alertSuccess(activar ? 'Cierre automático programado a 48h.' : 'Cierre automático cancelado.');
      notifyTicketUpdated(ticket);
      cargarTicket();
    } catch (error) {
      alertError('Error al modificar programación de cierre.');
    }
  };

  const confirmClose = () => {
    setIsResolvingFeedback(true);
    setShowFeedbackModal(true);
  };

  const handleDeleteTicket = async () => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar el ticket #${ticket.id}? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      await api.delete(`/tickets/${id}`);
      alertSuccess(`Ticket #${id} eliminado correctamente.`);
      handleGoBack();
    } catch (error) {
      console.error('Error al eliminar ticket:', error);
      alertError('Error al eliminar el ticket.');
    }
  };

  if (loading) return <div className="p-6">Cargando...</div>;
  if (!ticket) return <div className="p-6">No se encontró el ticket.</div>;

  const isOwner = user?.id === ticket.creadoPor?.id || (user?.email && ticket.emailSolicitante && user.email.toLowerCase() === ticket.emailSolicitante.toLowerCase());

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-start">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Ticket #{ticket.id}</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1 break-words">{ticket.descripcion || '—'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {['admin', 'soporte'].includes(user?.role?.toLowerCase()) && <TicketEditarDetalles ticket={ticket} onGuardado={cargarTicket} />}
          {ticket.estado === 'Esperando Usuario' && ['admin', 'soporte'].includes(user?.role?.toLowerCase()) && <TicketRecordarUsuario ticketId={ticket.id} />}
          {['admin', 'soporte'].includes(user?.role?.toLowerCase()) && <TicketRecordatorio ticketId={ticket.id} asignadoId={ticket.asignadoA?.id ?? ticket.asignadoAId} />}
          {user?.role?.toLowerCase() === 'admin' && (
            <button
              onClick={handleDeleteTicket}
              className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
              title="Eliminar ticket"
            >
              <Trash2 className="w-4 h-4" />
              Eliminar Ticket
            </button>
          )}
          <button onClick={handleGoBack} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm">
            Volver
          </button>
        </div>
      </div>

      {(ticket.estado === 'Resuelto' || ticket.estado === 'Esperando Usuario') && isOwner && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500 p-4 rounded-r-lg shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-orange-900 dark:text-orange-200">Acción Requerida</p>
              <p className="text-xs text-orange-800 dark:text-orange-300 mt-1">
                El ticket se encuentra <strong>{ticket.estado}</strong>. Por favor confirma si el inconveniente fue solucionado o califica la atención.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <button
                onClick={() => {
                  setIsResolvingFeedback(false);
                  setShowFeedbackModal(true);
                }}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 transition-colors text-white rounded-lg text-sm font-semibold shadow-sm flex items-center gap-1"
              >
                ✓ Confirmar y Calificar
              </button>
              <button
                onClick={() => {
                  document.getElementById('inline-comment-section')?.scrollIntoView({ behavior: 'smooth' });
                  document.getElementById('inline-comment-textarea')?.focus();
                }}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 transition-colors text-white rounded-lg text-sm font-semibold shadow-sm flex items-center gap-1"
              >
                ✕ No Solucionado (Responder)
              </button>
            </div>
          </div>
        </div>
      )}

      {ticket.estadoDerivacion === 'Pendiente' && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 rounded-r-lg shadow-sm mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-amber-900 dark:text-amber-200">
                  Solicitud de Derivación Pendiente
                </h4>
                <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">
                  {ticket.derivadoPor ? `${ticket.derivadoPor.nombre || ticket.derivadoPor.username}` : 'Un técnico'} ha propuesto derivar este ticket a <strong>{ticket.derivadoA ? `${ticket.derivadoA.nombre || ticket.derivadoA.username}` : 'otro técnico'}</strong>.
                  {ticket.motivoDerivacion && <span className="block italic mt-1 font-normal text-amber-700 dark:text-amber-400">Motivo: "{ticket.motivoDerivacion}"</span>}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0 self-end md:self-auto">
              {(Number(user?.id) === Number(ticket.derivadoAId) || user?.role?.toLowerCase() === 'admin') && (
                <>
                  <button
                    onClick={() => handleResponderDerivacion(true)}
                    className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold shadow transition-colors flex items-center gap-1"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Aceptar Derivación
                  </button>
                  <button
                    onClick={() => setShowRechazarModal(true)}
                    className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-semibold shadow transition-colors flex items-center gap-1"
                  >
                    <XCircle className="w-4 h-4" />
                    Rechazar
                  </button>
                </>
              )}

              {(Number(user?.id) === Number(ticket.derivadoPorId) || user?.role?.toLowerCase() === 'admin') && (
                <button
                  onClick={handleCancelarDerivacion}
                  className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-xs font-semibold shadow transition-colors"
                >
                  Cancelar Solicitud
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="relative">
            <TicketTimeline
              ticket={ticket}
              onAddActualizacion={() => setShowActualizacion(true)}
              onActualizacionEliminada={cargarTicket}
              onActualizacionAgregada={cargarTicket}
            />
            <button onClick={() => setShowChatInterno(true)} className="absolute top-2 right-2 p-3 text-blue-600">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
            </button>
          </div>

          {/* Calificación del usuario: solo la ve el staff/admin */}
          {ticket.feedback && ['admin', 'soporte'].includes(user?.role?.toLowerCase()) && (
            <div className="bg-white dark:bg-gray-800 border rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" />
                Calificación del usuario
                <span className="ml-auto text-xs font-normal text-gray-400">Solo visible para el equipo de soporte</span>
              </h2>
              {[
                { etiqueta: 'General', valor: ticket.feedback.calificacion },
                { etiqueta: 'Rapidez', valor: ticket.feedback.calificacionRapidez },
                { etiqueta: 'Atención', valor: ticket.feedback.calificacionAtencion },
              ].filter((c) => c.valor != null).map((c) => (
                <div key={c.etiqueta} className="flex items-center gap-3 py-1">
                  <span className="w-20 text-sm text-gray-600 dark:text-gray-300">{c.etiqueta}</span>
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${n <= c.valor ? 'text-amber-500 fill-amber-500' : 'text-gray-300 dark:text-gray-600'}`} />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{c.valor}/5</span>
                </div>
              ))}
              {ticket.feedback.comentario && (
                <div className="mt-3 rounded-lg bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700 p-3 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
                  {ticket.feedback.comentario}
                </div>
              )}
              {ticket.feedback.fechaCreacion && (
                <p className="mt-2 text-xs text-gray-400">
                  Calificado el {new Date(ticket.feedback.fechaCreacion).toLocaleString('es-ES')}
                </p>
              )}
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
              <User className="w-5 h-5 text-gray-400" />
              Solicitante
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="text-gray-900 dark:text-white font-medium">{ticket.nombreSolicitante || 'No especificado'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="text-gray-700 dark:text-gray-300">{ticket.emailSolicitante || 'No especificado'}</span>
              </div>
              {ticket.telefonoSolicitante && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{ticket.telefonoSolicitante}</span>
                </div>
              )}
              {(ticket.empresa || ticket.departamento) && (
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {[ticket.empresa, ticket.departamento].filter(Boolean).join(' · ')}
                  </span>
                </div>
              )}
              {ticket.ubicacion && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{ticket.ubicacion}</span>
                </div>
              )}
            </div>
          </div>

          {['admin', 'soporte'].includes(user?.role?.toLowerCase()) && (
            <div className="bg-white dark:bg-gray-800 border rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Activos del Usuario</h2>
              {activosUsuario.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {activosUsuario.map(activo => (
                    <div key={activo.id} onClick={() => setSelectedAssetForModal(activo)} className="p-3 border rounded-lg hover:bg-blue-50 cursor-pointer">
                      <p className="text-xs font-mono text-gray-500">{activo.codigo}</p>
                      <p className="font-medium">{activo.nombre || activo.modelo}</p>
                    </div>
                  ))}
                </div>
              ) : <p className="text-gray-500">Sin activos asignados.</p>}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Detalles</h2>
            <div className="space-y-2 text-sm">
              <p><strong>Solicitante:</strong> {ticket.nombreSolicitante}</p>
              <p><strong>Email:</strong> {ticket.emailSolicitante}</p>
              <p><strong>Categoría:</strong> {ticket.categoria}{ticket.subcategoria?.nombre ? ` › ${ticket.subcategoria.nombre}` : ''}</p>
              <p><strong>Estado:</strong> <span className={`px-2 py-0.5 rounded-full ${getEstadoColor(ticket)}`}>{getDisplayEstado(ticket)}</span></p>
              <p><strong>Prioridad:</strong> <span className={`px-2 py-0.5 rounded-full ${getPrioridadColor(ticket.prioridad)}`}>{ticket.prioridad}</span></p>
              <p className="flex flex-wrap items-center gap-2">
                <span><strong>Asignado a:</strong> {ticket.asignadoA?.username || ticket.asignadoA?.nombre || (ticket.asignadoAId ? 'Técnico' : 'Sin asignar')}</span>
                {!ticket.asignadoA && !ticket.asignadoAId && ['admin', 'soporte'].includes(user?.role?.toLowerCase()) && !['Resuelto', 'Cerrado'].includes(ticket.estado) && (
                  (() => {
                    const esSap = String(ticket.categoria || '').toUpperCase().includes('SAP');
                    const puede = user?.role?.toLowerCase() === 'admin' || esSap === ((user?.soportePerfil || '').toUpperCase() === 'SAP');
                    return puede ? (
                      <button type="button" onClick={() => asignarTicket(user.id)}
                        className="px-2 py-0.5 rounded-md text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700">
                        Asignármelo
                      </button>
                    ) : null;
                  })()
                )}
              </p>
            </div>

            <div className="mt-4 pt-4 border-t">
              <ParticipantesPicker
                value={participantes}
                canEdit={['admin', 'soporte'].includes(user?.role?.toLowerCase())}
                onAdd={async (p) => {
                  try {
                    await api.post(`/tickets/${id}/participantes`, { participantes: [p] });
                    setParticipantes((prev) => [...prev, { email: p.email, nombre: p.nombre, tipo: p.tipo }]);
                  } catch (e) { setAlerta({ tipo: 'error', mensaje: 'No se pudo agregar al participante' }); }
                }}
                onRemove={async (p) => {
                  try {
                    await api.delete(`/tickets/${id}/participantes/${p.id}`);
                    setParticipantes((prev) => prev.filter((x) => x.email !== p.email));
                  } catch (e) { setAlerta({ tipo: 'error', mensaje: 'No se pudo quitar al participante' }); }
                }}
              />
            </div>

            <div className="mt-6 pt-6 border-t space-y-4">
              {/* Sección de Asignación */}
              {(() => {
                const isSapTicket = ticket.categoria?.toUpperCase().includes('SAP');
                const isSapSupport = user?.soportePerfil?.toUpperCase() === 'SAP';
                const canAssign = isSapTicket ? isSapSupport : !isSapSupport;

                return ((user?.role?.toLowerCase() === 'soporte' && canAssign) || user?.role?.toLowerCase() === 'admin') && ticket.asignadoAId !== user.id && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">Asignación</p>
                    <button
                      onClick={() => asignarTicket(user.id)}
                      className="w-full bg-blue-600 text-white p-2 rounded text-sm hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5 a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                      Autoasignarme
                    </button>
                  </div>
                );
              })()}

              {user?.role?.toLowerCase() === 'admin' && (
                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Asignar Técnico</p>
                  <select
                    className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                    value={ticket?.asignadoA?.id || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === 'unassign') {
                        asignarTicket(null);
                      } else {
                        asignarTicket(parseInt(val, 10));
                      }
                    }}
                  >
                    <option value="">Seleccionar técnico...</option>
                    <option value="unassign">Quitar asignación</option>
                    {usuariosSoporte.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.nombre} {s.apellido} {s.soportePerfil ? `(${s.soportePerfil})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {['admin', 'soporte'].includes(user?.role?.toLowerCase()) && (
                <div className="pt-2">
                  <button
                    onClick={() => setShowDerivarModal(true)}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white p-2.5 rounded-lg text-sm transition-colors flex items-center justify-center gap-2 font-semibold shadow-sm"
                  >
                    <ArrowRightLeft className="w-4 h-4" />
                    Derivar Ticket a otro Técnico
                  </button>
                </div>
              )}

              {['admin', 'soporte'].includes(user?.role?.toLowerCase()) && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Cambiar Estado</label>
                    <select value={nuevoEstado} onChange={(e) => setNuevoEstado(e.target.value)} className="w-full p-2 border rounded text-sm bg-white dark:bg-gray-700">
                      {TICKET_ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                    {requiereComentario && (
                      <div className="mt-2">
                        <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                          Comentario obligatorio al marcar como {nuevoEstado}
                        </label>
                        <textarea
                          value={comentarioEstado}
                          onChange={(e) => setComentarioEstado(e.target.value)}
                          rows={3}
                          placeholder="Describe cómo se resolvió o por qué se cierra el ticket..."
                          className="w-full p-2 border rounded text-sm bg-white dark:bg-gray-700"
                        />
                      </div>
                    )}
                    <button onClick={cambiarEstado} className="w-full mt-2 bg-green-600 text-white p-2 rounded text-sm">Actualizar Estado</button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Prioridad</label>
                    <select value={nuevaPrioridad} onChange={(e) => setNuevaPrioridad(e.target.value)} className="w-full p-2 border rounded text-sm bg-white dark:bg-gray-700">
                      <option value="Baja">Baja</option><option value="Media">Media</option><option value="Alta">Alta</option><option value="Crítica">Crítica</option>
                    </select>
                    <button onClick={cambiarPrioridad} className="w-full mt-2 bg-green-600 text-white p-2 rounded text-sm">Actualizar Prioridad</button>
                  </div>

                  {/* Switch Cierre Automático */}
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Auto-Cierre 48H</span>
                      <button
                        onClick={() => toggleAutoClose(!ticket.fechaProgramacionCierre)}
                        className={`relative inline-flex h-6 w-11 rounded-full border-2 transition-colors ${ticket.fechaProgramacionCierre ? 'bg-blue-600 border-blue-600' : 'bg-gray-200 border-gray-200'}`}
                      >
                        <span className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${ticket.fechaProgramacionCierre ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <ChatInternoModal isOpen={showChatInterno} onClose={() => setShowChatInterno(false)} ticket={ticket} />
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => {
          setShowFeedbackModal(false);
          setIsResolvingFeedback(false);
        }}
        ticketId={id}
        onFeedbackSubmitted={cargarTicket}
        existingFeedback={ticket?.feedback}
        isResolving={isResolvingFeedback}
      />

      {selectedAssetForModal && (
        <ActivosAsignadosModal
          isOpen={!!selectedAssetForModal}
          onClose={() => setSelectedAssetForModal(null)}
          usuario={ticket?.creadoPor || { nombre: ticket?.nombreSolicitante || 'Usuario' }}
          activosAsignados={[selectedAssetForModal]}
        />
      )}

      {/* Modal para Derivar Ticket */}
      {showDerivarModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 dark:border-gray-700 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                Derivar Ticket #{ticket.id}
              </h3>
              <button onClick={() => setShowDerivarModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                &times;
              </button>
            </div>

            <form onSubmit={handleDerivarTicket} className="space-y-4">
              <p className="text-xs text-gray-600 dark:text-gray-300">
                El ticket permanecerá asignado a su técnico actual hasta que el técnico destino <strong>acepte</strong> la derivación.
              </p>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Técnico de Soporte Destino *
                </label>
                <select
                  required
                  value={soporteDestinoId}
                  onChange={(e) => setSoporteDestinoId(e.target.value)}
                  className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none"
                >
                  <option value="">Seleccionar técnico...</option>
                  {usuariosSoporte
                    .filter((s) => s.id !== ticket?.asignadoAId)
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nombre} {s.apellido} {s.soportePerfil ? `(${s.soportePerfil})` : ''}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Motivo de la Derivación (Opcional)
                </label>
                <textarea
                  rows={3}
                  value={motivoDerivacion}
                  onChange={(e) => setMotivoDerivacion(e.target.value)}
                  placeholder="Explica el motivo por el cual derivas este ticket..."
                  className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowDerivarModal(false)}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submittingDerivacion || !soporteDestinoId}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold shadow transition-colors"
                >
                  {submittingDerivacion ? 'Solicitando...' : 'Solicitar Derivación'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para Rechazar Derivación */}
      {showRechazarModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 dark:border-gray-700 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                Rechazar Derivación de Ticket #{ticket.id}
              </h3>
              <button onClick={() => setShowRechazarModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                &times;
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-xs text-gray-600 dark:text-gray-300">
                Al rechazar, el ticket permanecerá asignado a su técnico original y se registrará tu respuesta en el historial.
              </p>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Motivo del Rechazo (Opcional)
                </label>
                <textarea
                  rows={3}
                  value={motivoRechazo}
                  onChange={(e) => setMotivoRechazo(e.target.value)}
                  placeholder="Ingresa la razón por la que no puedes aceptar la derivación..."
                  className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowRechazarModal(false)}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => handleResponderDerivacion(false, motivoRechazo)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow transition-colors"
                >
                  Confirmar Rechazo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}